import React, { FC, Fragment, ReactNode, RefObject, useCallback, useEffect, useRef, useState, memo } from "react";
import { MDXProvider } from "@mdx-js/react";
import {
  ApiSecurity,
  formatExampleProperties,
  getPropertyKeysForRender,
  isBetaProperty,
  formatNav,
  parametersToQueryString,
  getEnumValuesForRender, filterUnpublishedElements,
} from "../../utils/api";
import Helmet from "react-helmet";
import PostMarkdown, {
  MDXComponents,
  MarkdownH2,
  MarkdownH3,
  MarkdownInlineCode,
  MarkdownTable,
  MarkdownTableRow,
  MarkdownTableCell,
  MarkdownBlockquote,
  MarkdownImage,
} from "../../layouts/Post/PostMarkdown";
import Heading, { HeadingWeight } from "../../components/Heading";
import { kebabCase } from "lodash";
import { SidebarCategory, SidebarLink, SidebarWrapper } from "../../components/Sidebar";
import { Language } from "prism-react-renderer";
import {
  Button,
  ButtonDecorator,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  Card,
  CopyToClipboard,
  IconButton,
  Link,
  OptionMenu,
  OptionMenuItem,
  SegmentedControl,
} from "kaleidoscope";
import { ChevronDown, ChevronUp, Copy, IconSize } from "kaleidoscope/src/global/icons";
import Label, { LabelSize } from "../../components/Label";
import Text, { TextSize, TextTone, TextWeight } from "../../components/Text";
import InternalLink from "../../components/Link";
import { graphql } from "gatsby";
import Footer from "../../components/Footer";
import Code from "../../components/Code";
import classNames from "classnames";
import { handleMouseUpByBlurring } from "kaleidoscope/src/utils/focus";
import { randomString } from "kaleidoscope/src/utils";

interface ReferenceProps {
  pageContext: any;
  data: any;
}

interface NavHeading {
  element: HTMLElement;
  top: number;
  url: string;
}

const Reference: FC<ReferenceProps> = ({ pageContext, data }) => {
  const { apiConfig } = pageContext;
  const navItems = formatNav(apiConfig);
  const headings = useRef<NavHeading[]>();
  const [currentHeading, setCurrentHeading] = useState<NavHeading>();
  const scrollContainer = useRef<HTMLDivElement>(null);

  const updateHeadings = useCallback(() => {
    headings.current = navItems
      .map((item) => item.pages)
      .flat()
      .map((item) => {
        const element = document.getElementById(item.url.replace("#", ""));
        return { element, top: element?.offsetTop, url: item.url };
      });
  }, []);

  useEffect(() => {
    updateHeadings();

    const contentObserver = new ResizeObserver(updateHeadings);
    contentObserver.observe(scrollContainer.current);

    return () => {
      contentObserver.disconnect();
    };
  }, []);

  const handleScroll = useCallback(() => {
    const latestScroll = scrollContainer.current.scrollTop;

    let latestHeading;

    for (const heading of headings.current) {
      if (heading.top < latestScroll + window.innerHeight / 2) {
        latestHeading = heading;
      }
    }

    setCurrentHeading(latestHeading);
  }, []);

  return (
    <MDXProvider components={MDXComponents}>
      <Helmet>
        <title>Qwilr API | Reference</title>
        <meta name="description" content="Qwilr API Reference" />
      </Helmet>
      <SidebarWrapper>
        {navItems.map(({ label, pages }) => (
          <SidebarCategory label={label} key={label}>
            {pages.map(({ label, url }) => (
              <SidebarLink key={url} url={url} current={currentHeading?.url === url}>
                {label}
              </SidebarLink>
            ))}
          </SidebarCategory>
        ))}
      </SidebarWrapper>
      <ReferenceContent
        handleScroll={handleScroll}
        scrollContainer={scrollContainer}
        apiConfig={apiConfig}
        data={data}
      />
    </MDXProvider>
  );
};

interface ReferenceContentProps {
  handleScroll: () => void;
  scrollContainer: RefObject<HTMLDivElement>;
  apiConfig: any;
  data: any;
}

const ReferenceContent: FC<ReferenceContentProps> = memo(({ handleScroll, scrollContainer, apiConfig, data }) => {
  const { info, security, methods, servers } = apiConfig;

  return (
    <div className="kld-reference" onScroll={handleScroll} ref={scrollContainer}>
      <div className="kld-reference__main">
        <section className="kld-reference__intro">
          <div className="kld-reference__intro-content">
            <div className="kld-reference__intro-header">
              <Heading level={1} weight={HeadingWeight.Strong}>
                {info.title}
              </Heading>
              <Text size={TextSize.Large} weight={TextWeight.Strong} className="kld-reference__version">
                v{info.version}
              </Text>
            </div>
            <PostMarkdown>{info.description}</PostMarkdown>
            <MarkdownBlockquote>
              <Label as="span">Getting started?</Label>
              <Text size={TextSize.Medium}>
                Check out our <InternalLink href="/docs/getting-started/quick-start">quick start guide</InternalLink> to
                walk through the process of getting set up
              </Text>
            </MarkdownBlockquote>
            <ReferenceExample title="Base URL" code={servers[0].url} lang="yaml" />
          </div>
        </section>

        <section className="kld-reference__section">
          <MarkdownH2 className="kld-reference__heading" id="authentication">
            Authentication
          </MarkdownH2>
          {security.map((section) => (
            <section className="kld-reference__method kld-reference__method--borderless" key={section.title}>
              <div className="kld-reference__copy">
                <MarkdownH3 id={`authentication/${kebabCase(section.title)}`}>{section.title}</MarkdownH3>
                <PostMarkdown>{section.description}</PostMarkdown>
                <MarkdownTable>
                  <MarkdownTableRow>
                    <MarkdownTableCell>
                      <strong>Security Scheme Type</strong>
                    </MarkdownTableCell>
                    <MarkdownTableCell>{section.details.type}</MarkdownTableCell>
                  </MarkdownTableRow>
                  <MarkdownTableRow>
                    <MarkdownTableCell>
                      <strong>HTTP Authorization Scheme</strong>
                    </MarkdownTableCell>
                    <MarkdownTableCell>{section.details.scheme}</MarkdownTableCell>
                  </MarkdownTableRow>
                  <MarkdownTableRow>
                    <MarkdownTableCell>
                      <strong>Bearer format</strong>
                    </MarkdownTableCell>
                    <MarkdownTableCell>{section.details.bearerFormat}</MarkdownTableCell>
                  </MarkdownTableRow>
                </MarkdownTable>
              </div>
              <div className="kld-reference__example">
                <MarkdownImage
                  src="/assets/images/settings-token-small.png"
                  alt="API settings page with a modal open to copy the API token"
                />
              </div>
            </section>
          ))}
        </section>

        {Object.keys(methods).map((key) => {
          const section = methods[key];

          return (
            <section className="kld-reference__section" key={key} id={key}>
              <div className="kld-reference__copy">
                <MarkdownH2 className="kld-reference__heading" id={kebabCase(section.title)}>
                  {section.title}
                </MarkdownH2>
                {!!section.description && <PostMarkdown>{section.description}</PostMarkdown>}
              </div>
              {section.methods.map((method) => {
                const content = method.requestBody?.content;

                const callbacks = Object.keys(method.callbacks || {})
                  .map((callback) =>
                    Object.keys(method.callbacks[callback]).map((callbackPath) =>
                      Object.keys(method.callbacks[callback][callbackPath]).map((callbackMethod) => ({
                        ...method.callbacks[callback][callbackPath][callbackMethod],
                        path: callbackPath,
                        method: callbackMethod,
                      })),
                    ),
                  )
                  .flat()
                  .flat();

                return (
                  <section className="kld-reference__method" key={method.operationId}>
                    <div className="kld-reference__copy">
                      <MarkdownH3
                        className="kld-reference__heading"
                        id={`${kebabCase(section.title)}/${kebabCase(method.summary)}`}
                      >
                        {method.summary}
                      </MarkdownH3>
                      <PostMarkdown>{method.description}</PostMarkdown>
                      {!!method.requestBody &&
                        Object.keys(content).map((schemaKey) => (
                          <ReferenceAttributes
                            schema={content[schemaKey].schema}
                            parameters={method.parameters}
                            security={security}
                            key={schemaKey}
                          />
                        ))}
                      {!method.requestBody && <ReferenceAttributes parameters={method.parameters} />}
                      {!!method.responses && (
                        <>
                          <Heading as="h3" className="kld-attributes__title" level={4}>
                            Responses
                          </Heading>
                          {Object.keys(method.responses).map((response) => (
                            <ReferencesResponseAttributes
                              code={response}
                              key={response}
                              response={method.responses[response]}
                            />
                          ))}
                        </>
                      )}
                      {!!method.callbacks && (
                        <>
                          <Heading as="h3" className="kld-attributes__title" level={4}>
                            Callbacks
                          </Heading>
                          {filterUnpublishedElements(callbacks).map((callback) => (
                            <CallbackCard callback={callback} key={callback.summary} />
                          ))}
                        </>
                      )}
                    </div>
                    <div className="kld-reference__example">
                      {!!method.requestBody &&
                        Object.keys(content).map((schemaKey) => (
                          <ReferenceExample
                            schema={content[schemaKey].schema}
                            key={schemaKey}
                            title={
                              <>
                                <ReferencePill>{method.type}</ReferencePill>
                                {method.path}
                              </>
                            }
                          />
                        ))}
                      {!method.requestBody && (
                        <ReferenceExample
                          schema={{}}
                          title={
                            <>
                              <ReferencePill>{method.type}</ReferencePill>
                              {method.path}
                              {parametersToQueryString(method.parameters)}
                            </>
                          }
                        />
                      )}
                      {!!method.responses && <ReferenceResponses responseData={method.responses} />}
                      {!!method.callbacks && <ReferenceCallbackExample callbackData={callbacks} />}
                    </div>
                  </section>
                );
              })}
            </section>
          );
        })}
        <Footer className="kld-reference__footer" modified={data.file.mtime} />
      </div>
    </div>
  );
});

interface ReferencesResponseAttributesProps {
  response: any;
  code: string;
}

const ReferencesResponseAttributes: FC<ReferencesResponseAttributesProps> = ({ code, response }) => {
  const [expanded, setExpanded] = useState(false);
  const contentId = useRef(randomString());
  const { description, content } = response;
  const hasContent = !!content;
  const Element = hasContent ? "button" : "div";

  const toggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Card className="kld-response-attributes">
      <Element
        className={classNames("kld-response-attributes__title", {
          "kld-response-attributes__title--hasContent": hasContent,
        })}
        onClick={hasContent ? toggle : undefined}
        onMouseUp={hasContent ? handleMouseUpByBlurring : undefined}
        aria-expanded={hasContent ? expanded : undefined}
        aria-controls={hasContent ? contentId.current : undefined}
      >
        <Text
          className={classNames("kld-response-attributes__code", {
            "kld-response-attributes__code--error": code[0] === "4",
          })}
          weight={TextWeight.Strong}
        >
          {code}
        </Text>
        {description && (
          <Text>
            <PostMarkdown>{description}</PostMarkdown>
          </Text>
        )}
        {hasContent && (
          <ChevronDown
            className={classNames("kld-response-attributes__chevron", {
              "kld-response-attributes__chevron--expanded": expanded,
            })}
          />
        )}
      </Element>
      {hasContent && expanded && (
        <div className="kld-response-attributes__attributes" id={contentId.current}>
          <ReferenceAttributes hideTitle schema={content["application/json"].schema} />
        </div>
      )}
    </Card>
  );
};

interface ReferenceAttributesProps {
  schema?: any;
  parameters?: any;
  security?: ApiSecurity[];
  hideTitle?: boolean;
}

const ReferenceAttributes: FC<ReferenceAttributesProps> = ({ schema, security, parameters, hideTitle }) => {
  return (
    <div className="kld-attributes">
      {!!security && (
        <>
          <Heading as="h3" className="kld-attributes__title" level={4}>
            Authorizations
          </Heading>
          <Text className="kld-attributes__value">
            {security.map((auth) => (
              <Link href={`#authentication/${kebabCase(auth.title)}`} key={auth.title}>
                {auth.title}
              </Link>
            ))}
          </Text>
        </>
      )}
      {!!parameters && (
        <>
          <Heading as="h3" className="kld-attributes__title" level={4}>
            Path parameters
          </Heading>
          <ul className="kld-attributes__list">
            {parameters.map((param) => (
              <li className="kld-attributes__item" key={param.name}>
                <ReferenceAttributeProperty
                  required={param.required}
                  name={param.name}
                  property={param.schema}
                  description={param.description}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {!!schema && (
        <>
          {!hideTitle && (
            <Heading as="h3" className="kld-attributes__title" level={4}>
              Attributes
            </Heading>
          )}
          <ul className="kld-attributes__list">
            {!!schema.oneOf && <ReferenceAttributeOneOfProperty oneOf={schema.oneOf} />}
            {schema.type === "array" && (
              <div>
                <Label className="kld-attributes__label">Array:</Label>
                {getPropertyKeysForRender(schema.items.properties).map((propertyKey) => {
                  const property = schema.items.properties[propertyKey];

                  return (
                    <li className="kld-attributes__item" key={propertyKey}>
                      <ReferenceAttributeProperty
                        required={schema.required?.includes(propertyKey)}
                        name={propertyKey}
                        property={property}
                        description={property.description}
                      />
                    </li>
                  );
                })}
              </div>
            )}
            {!!schema.properties &&
              getPropertyKeysForRender(schema.properties).map((propertyKey) => {
                const property = schema.properties[propertyKey];

                return (
                  <li className="kld-attributes__item" key={propertyKey}>
                    <ReferenceAttributeProperty
                      required={schema.required?.includes(propertyKey)}
                      name={propertyKey}
                      property={property}
                      description={property.description}
                    />
                  </li>
                );
              })}
          </ul>
        </>
      )}
    </div>
  );
};

interface ReferencePillProps {
  theme?: "dark" | "light";
}

const ReferencePill: FC<ReferencePillProps> = ({ children, theme = "dark" }) => {
  return (
    <Label
      as="span"
      className={`kld-reference__method-pill kld-reference__method-pill--${kebabCase(
        children.toString(),
      )} kld-reference__method-pill--${theme}`}
      size={LabelSize.Small}
    >
      {children}
    </Label>
  );
};

const getPropertyType = (property) => {
  if (property.type === "array") {
    const isEnum = property.items.enum;
    const isOneOf = property.items.oneOf;

    if (isOneOf) {
      return `array of ${property.items.oneOf[0].type}s`;
    }

    return `array of ${isEnum ? "enum" : property.items.type}s`;
  }

  if (property.enum) {
    return "enum";
  }

  return property.type;
};

interface ReferenceAttributeOneOfPropertyProps {
  oneOf: any[];
}

const ReferenceAttributeOneOfProperty: FC<ReferenceAttributeOneOfPropertyProps> = ({ oneOf }) => {
  const [oneOfIndex, setOneOfIndex] = useState(0);
  const currentOneOf = oneOf[oneOfIndex];

  return (
    <li className="kld-attributes__item">
      <SegmentedControl
        labelHidden
        label="One of"
        options={oneOf.map((value) => ({ label: value.title, value: value.title }))}
        selectedValue={currentOneOf.title}
        onClickHandler={(value) => setOneOfIndex(oneOf.indexOf(oneOf.find((item) => item.title === value)))}
      />
      {getPropertyKeysForRender(currentOneOf.properties).map((propertyKey) => {
        const required = currentOneOf.required.includes(propertyKey);
        const property = currentOneOf.properties[propertyKey];

        return (
          <Fragment key={propertyKey}>
            <ReferenceAttributeProperty
              required={required}
              property={property}
              name={propertyKey}
              description={property.description}
            />
          </Fragment>
        );
      })}
    </li>
  );
};

interface ReferenceAttributeSubPropertyProps {
  property: any;
}

const ReferenceAttributeSubProperty: FC<ReferenceAttributeSubPropertyProps> = ({ property }) => {
  const subProperties = property.properties || property.items?.properties;

  if (!subProperties) return null;

  return (
    <Card className="kld-sub-property">
      {getPropertyKeysForRender(subProperties).map((propertyKey) => {
        const subProperty = subProperties[propertyKey];
        const required = property.required?.includes(propertyKey);

        return (
          <div className="kld-sub-property__item" key={propertyKey}>
            <ReferenceAttributeProperty
              name={propertyKey}
              property={subProperty}
              required={required}
              description={subProperty.description}
            />
          </div>
        );
      })}
    </Card>
  );
};

interface ReferenceAttributePropertyProps {
  name: string;
  required: boolean;
  property: any;
  description?: string;
}

const ReferenceAttributeProperty: FC<ReferenceAttributePropertyProps> = ({ name, required, property, description }) => {
  const [subAttrsExpanded, setSubAttrsExpanded] = useState(false);
  const enumValues = getEnumValuesForRender(property);
  const hasSubAttrs = property.properties || property.items?.properties;

  const toggleSubAttrs = () => {
    setSubAttrsExpanded(!subAttrsExpanded);
  };

  return (
    <>
      <span>
        <Text size={TextSize.Small} className="kld-attributes__attribute">
          <span className="kld-attributes__property">{property.deprecated ? <del>{name}</del> : name}</span>
          {!!property.deprecated && <ReferencePill>Deprecated</ReferencePill>}
          {isBetaProperty(property) && <ReferencePill>Beta</ReferencePill>}
          <Text size={TextSize.Small} tone={TextTone.Secondary} className="kld-attributes__property-details">
            {required && <span>Required</span>}
            <span>{getPropertyType(property)}</span>
            {property.nullable && <span>Nullable</span>}
            {property.title && <span>{`(${property.title})`} </span>}
            {property.pattern && <span>{property.pattern}</span>}
            {property.maxLength && <span>(max characters: {property.maxLength.toLocaleString()})</span>}
          </Text>
        </Text>
        {!!description && (
          <div className="kld-attributes__description">
            <PostMarkdown>{description}</PostMarkdown>
          </div>
        )}
        {!!property.default && (
          <Text>
            {`Default: `}
            <MarkdownInlineCode>{property.default.toString()}</MarkdownInlineCode>
          </Text>
        )}
        {hasSubAttrs && (
          <Button
            className="kld-attributes__toggle"
            size={ButtonSize.XSmall}
            onClick={toggleSubAttrs}
            type={ButtonType.Secondary}
          >
            <ChevronDown
              size={IconSize.Small}
              className={classNames("kld-attributes__toggle-icon", {
                "kld-attributes__toggle-icon--expanded": subAttrsExpanded,
              })}
            />
            {`${subAttrsExpanded ? "Hide" : "Show"} child attributes`}
          </Button>
        )}
      </span>

      {!!enumValues && (
        <div className="kld-attributes__enum">
          {enumValues.map((item) => (
            <MarkdownInlineCode key={item}>{`"${item}"`}</MarkdownInlineCode>
          ))}
        </div>
      )}

      {!!property.items?.oneOf && <ReferenceAttributeOneOfProperty oneOf={property.items.oneOf} />}

      {!!property.oneOf && <ReferenceAttributeOneOfProperty oneOf={property.oneOf} />}

      {hasSubAttrs && subAttrsExpanded && <ReferenceAttributeSubProperty property={property} />}
    </>
  );
};

interface ReferenceExampleProps {
  schema?: any;
  title?: ReactNode;
  actions?: ReactNode;
  code?: string;
  lang?: Language;
}

const ReferenceExample: FC<ReferenceExampleProps> = ({ schema, title, code, lang = "json", actions }) => {
  const [oneOfIndex, setOneOfIndex] = useState(0);
  const example = code || formatExampleProperties(schema, oneOfIndex);
  const hasCode = JSON.stringify(example) !== "{}";

  return (
    <div className="kld-reference-code">
      <div className="kld-reference-code__header">
        <div className="kld-reference-code__title">{title}</div>
        <div className="kld-reference-code__actions">
          {actions}
          {!!schema?.oneOf && (
            <OptionMenu
              button={
                <Button
                  type={ButtonType.Tertiary}
                  size={ButtonSize.XSmall}
                  decorator={ButtonDecorator.Dropdown}
                  theme={ButtonTheme.Dark}
                >
                  {schema.oneOf[oneOfIndex].title}
                </Button>
              }
            >
              {schema?.oneOf.map((item, index) => (
                <OptionMenuItem key={item.title} onClick={() => setOneOfIndex(index)}>
                  {item.title}
                </OptionMenuItem>
              ))}
            </OptionMenu>
          )}
          {hasCode && (
            <CopyToClipboard value={JSON.stringify(example, null, 2)}>
              {({ onCopy }) => (
                <IconButton size={ButtonSize.XSmall} icon={<Copy />} theme={ButtonTheme.Dark} onClick={onCopy} />
              )}
            </CopyToClipboard>
          )}
        </div>
      </div>
      {hasCode && (
        <div className="kld-reference-code__content">
          <Code
            className="kld-reference-code__element"
            code={code ? code : JSON.stringify(example, null, 2)}
            language={lang}
          />
        </div>
      )}
    </div>
  );
};

interface ReferenceCallbackExampleProps {
  callbackData: any;
}

const ReferenceCallbackExample: FC<ReferenceCallbackExampleProps> = ({ callbackData }) => {
  const [callbacks, setCallbacks] = useState([]);
  const [callbackIndex, setCallbackIndex] = useState(0);
  const currentResponse = callbacks[callbackIndex];

  useEffect(() => {
    const formattedCallbacks = filterUnpublishedElements(callbackData)
      .map((callback) => {
        const item = callback.requestBody.content?.["application/json"];
        return { ...item, summary: callback.summary, path: callback.path };
      })
      .filter((value) => !!value.schema);

    setCallbacks(formattedCallbacks);
  }, []);

  if (!callbacks.length) return null;

  return (
    <ReferenceExample
      title={
        <>
          <ReferencePill>Callback</ReferencePill>
          {currentResponse.summary}
        </>
      }
      schema={currentResponse.schema}
      actions={
        <OptionMenu
          size="large"
          button={
            <IconButton
              type={ButtonType.Tertiary}
              size={ButtonSize.XSmall}
              theme={ButtonTheme.Dark}
              icon={<ChevronDown />}
              aria-label={currentResponse.summary}
            />
          }
        >
          {callbacks.map((item, index) => (
            <OptionMenuItem
              key={item.summary}
              onClick={() => setCallbackIndex(index)}
              selected={callbackIndex === index}
            >
              {item.summary}
            </OptionMenuItem>
          ))}
        </OptionMenu>
      }
    />
  );
};

interface ReferenceResponsesProps {
  responseData: any;
}

const ReferenceResponses: FC<ReferenceResponsesProps> = ({ responseData }) => {
  const [responses, setResponses] = useState([]);
  const [responseIndex, setResponseIndex] = useState(0);
  const currentResponse = responses[responseIndex];

  useEffect(() => {
    const formattedResponses = Object.keys(responseData)
      .map((response) => {
        const item = responseData[response].content?.["application/json"];

        // Add the response code in to the examples
        if (item?.schema?.properties?.code) {
          item.schema.properties.code.example = response;
        }

        return { ...item, code: response };
      })
      .filter((value) => !!value.schema);

    setResponses(formattedResponses);
  }, []);

  if (!responses.length) return null;

  return (
    <ReferenceExample
      title={<ReferencePill>Response</ReferencePill>}
      schema={currentResponse.schema}
      actions={
        <OptionMenu
          size="small"
          button={
            <Button
              type={ButtonType.Tertiary}
              size={ButtonSize.XSmall}
              decorator={ButtonDecorator.Dropdown}
              theme={ButtonTheme.Dark}
            >
              {currentResponse.code}
            </Button>
          }
        >
          {responses.map((item, index) => (
            <OptionMenuItem key={item.code} onClick={() => setResponseIndex(index)} selected={responseIndex === index}>
              {item.code}
            </OptionMenuItem>
          ))}
        </OptionMenu>
      }
    />
  );
};

interface CallbackCardProps {
  callback: any;
}

const CallbackCard: FC<CallbackCardProps> = ({ callback }) => {
  const [expanded, setExpanded] = useState(false);
  const contentId = useRef(randomString());

  return (
    <Card className="kld-callback">
      <button
        className="kld-callback__header"
        aria-expanded={expanded}
        aria-controls={contentId.current}
        onClick={() => setExpanded(!expanded)}
      >
        <Text size={TextSize.Small}>{callback.summary}</Text>
        {expanded ? <ChevronUp /> : <ChevronDown />}
      </button>
      {expanded && (
        <div className="kld-callback__content" id={contentId.current}>
          <Text size={TextSize.Small} className="kld-callback__path">
            <ReferencePill theme="light">{callback.method}</ReferencePill>
            {callback.path}
          </Text>
          <div className="kld-callback__description">
            <PostMarkdown>{callback.requestBody.description}</PostMarkdown>
          </div>
          <ReferenceAttributes schema={callback.requestBody.content["application/json"].schema} />
          {!!callback.responses && (
            <>
              <Heading as="h3" className="kld-attributes__title" level={4}>
                Responses
              </Heading>
              {Object.keys(callback.responses).map((response) => (
                <ReferencesResponseAttributes code={response} key={response} response={callback.responses[response]} />
              ))}
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export const pageQuery = graphql`
  query RefQuery {
    file {
      mtime
    }
  }
`;

export default Reference;
